import dayjs, { Dayjs, ManipulateType } from 'dayjs';
import { omit } from 'lodash';
import {
  BedOccupancyDataPointsCount,
  ContinousMotionAggregation,
  ContinousPositionCount,
  ContinuousAggregatedMeasurement,
  Sleep,
} from 'src/types/individual-dashboard';
import {
  API_DATE_FORMAT_WITHOUT_MS,
  displayInTimezone,
  getFormatDateByContinent,
  MONTH_DAY_YEAR_FORMAT,
} from 'src/utils/timeUtils';
import {
  GraphTimeData,
  ProcessedSleepData,
  TIMEFRAME_RANGE,
  TrendingOverviewItemData,
} from './types';
import { getStartTimeOfRange, POINTS_DATE_FORMAT } from './utils';
import { DateUTC, UUID } from 'src/types/utility';
import { AlertLog } from 'src/types/alerts';
import {
  computeTimeValues,
  convertToHourResolutionStepSize,
  mapUtcStringTimeToHourResolution,
} from './time-processors';
import { ALERT_METRIC_ENUM } from 'src/redux/data/constants';
import { getPatientStatus } from 'src/routes/IndividualDashboard/components/DashboardTab/TrendingOverviewWidget/utils';
import { checkMeasurementIsValid } from 'src/redux/data/monitor/modules/utils';
import { MeasurementType } from 'src/redux/data/monitor/modules/types';
import { MotionAggregatedDataResponse } from 'src/routes/Customers/modules/types';
import { FetchVitalSignsMeasurementsResponse } from 'src/services/types';
import { PATIENT_STATUS } from 'src/routes/IndividualDashboard/components/DashboardTab/TrendingOverviewWidget/types';
import { MEDICAL_REPORT_API_VERSION } from 'src/routes/MedicalReports/utils';
import { isNullOrUndefined } from 'src/utils/parser';

export const processBedDataToHours = (dataInMinutes: number | null) => {
  if (!dataInMinutes) {
    return dataInMinutes;
  }
  const hours = Math.floor(dataInMinutes / 60);
  const minutesLeft = dataInMinutes - Math.floor(hours * 60);
  //round minutes to closest 10 value
  const roundedMinutesLeft = Math.round(minutesLeft / 10) * 10;
  return (hours * 60 + roundedMinutesLeft) / 60;
};

export const processSleepData = (data: Sleep[], patientId: UUID) => {
  const processedSleepData = {} as Record<string, ProcessedSleepData>;
  data.map(sleepDay => {
    processedSleepData[
      dayjs(sleepDay.startTime).format(
        getFormatDateByContinent(MONTH_DAY_YEAR_FORMAT),
      )
    ] = {
      remSleep: processBedDataToHours(sleepDay.remMinutes),
      sleep: processBedDataToHours(sleepDay.sleepMinutes),
      startOfNight: sleepDay.startOfNight,
      startTime: sleepDay.startTime,
      endOfNight: sleepDay.endOfNight,
      apneaCount: sleepDay.apneaCount,
      failureReason: sleepDay.failureReason,
      ahi: sleepDay.ahi,
    };
  });
  return { [patientId]: { data: processedSleepData } };
};
export const processMotionData = (
  data: MotionAggregatedDataResponse['motionDailyResponse'] = [],
  patientId: UUID,
) => {
  const motionDataPerDay = {} as Record<
    string,
    { highMotion: number; mediumMotion: number; lowMotion: number }
  >;
  data.map(eachDayData => {
    const {
      highMotionCount = 0,
      mediumMotionCount = 0,
      lowMotionCount = 0,
      time,
    } = eachDayData;
    const day = dayjs(time).format(
      getFormatDateByContinent(MONTH_DAY_YEAR_FORMAT),
    );
    motionDataPerDay[day] = {
      highMotion: highMotionCount / 3600,
      mediumMotion: mediumMotionCount / 3600,
      lowMotion: lowMotionCount / 3600,
    };
  });

  return { [patientId]: { data: motionDataPerDay } };
};
export const processBedOccupancyData = (
  data: MotionAggregatedDataResponse['motionDailyResponse'],
  patientId: UUID,
): Record<
  UUID,
  {
    hoursInBedData: GraphTimeData[];
  }
> => {
  const hoursInBedData = data.map(d => {
    const { inBedDataPointsCount, time } = d;
    const aproxHours = inBedDataPointsCount / 3600;
    const day = dayjs(time).format(API_DATE_FORMAT_WITHOUT_MS);
    const formattedHoursInBedData = {
      date: day,
      value: aproxHours,
    };
    return formattedHoursInBedData;
  });
  return {
    [patientId]: {
      hoursInBedData,
    },
  };
};

const mapAlertToChartMarkLabel = (alertItem: AlertLog) => {
  switch (alertItem.thresholdMetric) {
    case ALERT_METRIC_ENUM.RR:
    case ALERT_METRIC_ENUM.RR_BASELINE:
      return 'rrAlert';
    case ALERT_METRIC_ENUM.HR:
    case ALERT_METRIC_ENUM.HR_BASELINE:
      return 'hrAlert';
    default:
      return 'motionAlert';
  }
};

const mapAlertToTrendLineLabel = (alertItem: AlertLog) => {
  switch (alertItem.thresholdMetric) {
    case ALERT_METRIC_ENUM.RR:
    case ALERT_METRIC_ENUM.RR_BASELINE:
      return 'rr';
    case ALERT_METRIC_ENUM.HR:
    case ALERT_METRIC_ENUM.HR_BASELINE:
      return 'hr';
    default:
      return 'motion';
  }
};

const groupMotionData = (
  motionData: ContinousMotionAggregation[],
  stepSize: number,
): Record<string, ContinousMotionAggregation[]> =>
  motionData.reduce(
    (
      acc: Record<string, ContinousMotionAggregation[]>,
      item: ContinousMotionAggregation,
    ) => {
      const aggregatedTime = mapUtcStringTimeToHourResolution(
        item.time,
        stepSize,
        'start',
      );
      return {
        ...acc,
        [aggregatedTime]: [...(acc[aggregatedTime] || []), item],
      };
    },
    {},
  );

const computeMeanMotionValue = (motionData: ContinousMotionAggregation[]) =>
  motionData.reduce((acc, item) => acc + item.aggregatedValue, 0) /
  motionData.length;

const computeAggregation = (
  rawMotionData: ContinousMotionAggregation[] = [],
): number => {
  const highMotionPoints = rawMotionData.filter(
    item => item.aggregatedValue >= 5,
  );

  if (highMotionPoints.length > 1) {
    return computeMeanMotionValue(highMotionPoints);
  }

  if (highMotionPoints[0]) {
    return (highMotionPoints[0].aggregatedValue - 5) / 1.2 + 5;
  }

  return computeMeanMotionValue(rawMotionData);
};

const initializeMergedData = (timeValues: string[], timezone: string) =>
  timeValues.reduce((acc, time, index) => {
    return {
      ...acc,
      [time]: {
        time: displayInTimezone(time, timezone, POINTS_DATE_FORMAT),
        anchorLine: 0,
        order: index,
      },
    };
  }, {});

export const aggregateMotionData = (
  motionData: ContinousMotionAggregation[],
  stepSize: number,
): Record<string, number | null> => {
  const groupedMotionData = groupMotionData(motionData, stepSize);

  return Object.keys(groupedMotionData).reduce((acc, key) => {
    const aggregatedMotion = computeAggregation(groupedMotionData[key]);
    return {
      ...acc,
      [key]: Math.round(aggregatedMotion * 100) / 100,
    };
  }, {});
};

const roundToNextHour = (date: string) => {
  const time = dayjs.utc(date);
  return time.minute()
    ? time
        .hour(time.hour() + 1)
        .minute(0)
        .second(0)
    : time.minute(0).second(0);
};

const roundToNearest5Minutes = (date: string) => {
  const time = dayjs(date);
  const roundedMinute = Math.floor(time.minute() / 5) * 5;
  return time.minute(roundedMinute);
};

const mergeDataByTime = ({
  timezone,
  measurementsData,
  motionData = [],
  alertLogsData = [],
  bedOccupancyDataPointsCounts = [],
}: {
  timezone: string;
  measurementsData?: FetchVitalSignsMeasurementsResponse;
  motionData?: ContinousMotionAggregation[];
  alertLogsData?: AlertLog[];
  bedOccupancyDataPointsCounts?: BedOccupancyDataPointsCount[];
}) => {
  const mergedDataByTime = {} as Record<string, TrendingOverviewItemData>;
  if (measurementsData?.measurements) {
    for (const measure of measurementsData.measurements) {
      const { HR, RR, IE, RA_bin } = measure.measurements;

      const formattedTime = displayInTimezone(
        measure.time,
        timezone,
        POINTS_DATE_FORMAT,
      );
      mergedDataByTime[formattedTime] = {
        ra: Math.round(RA_bin?.average ?? 0),
        time: displayInTimezone(measure.time, timezone, POINTS_DATE_FORMAT),
        anchorLine: 0,
      };

      if (checkMeasurementIsValid(HR?.average ?? 0, MeasurementType.HR)) {
        mergedDataByTime[formattedTime] = {
          ...(mergedDataByTime[formattedTime] as TrendingOverviewItemData),
          hr: Math.round(HR?.average ?? 0),
        };
      }
      if (checkMeasurementIsValid(RR?.average ?? 0, MeasurementType.RR)) {
        mergedDataByTime[formattedTime] = {
          ...(mergedDataByTime[formattedTime] as TrendingOverviewItemData),
          rr: Math.round(RR?.average ?? 0),
        };
      }
      if (checkMeasurementIsValid(IE?.average ?? 0, MeasurementType.IE)) {
        mergedDataByTime[formattedTime] = {
          ...(mergedDataByTime[formattedTime] as TrendingOverviewItemData),
          ie: IE?.average.toFixed(1),
        };
      }
    }
  }
  for (const motionItem of motionData) {
    const formattedTime = displayInTimezone(
      motionItem.time,
      timezone,
      POINTS_DATE_FORMAT,
    );
    const currentData = mergedDataByTime[formattedTime];

    mergedDataByTime[formattedTime] = {
      ...currentData,
      motion: motionItem.aggregatedValue
        ? Math.round(motionItem.aggregatedValue)
        : motionItem.aggregatedValue,
      time: formattedTime,
    };
  }

  for (const alertItem of alertLogsData) {
    const startTime = roundToNextHour(alertItem.startTime).format(
      POINTS_DATE_FORMAT,
    );
    const formattedStartTime = displayInTimezone(
      startTime,
      timezone,
      POINTS_DATE_FORMAT,
    );
    const currentData = mergedDataByTime[formattedStartTime];
    const alertKey = mapAlertToChartMarkLabel(alertItem);
    const trendingLineKey = mapAlertToTrendLineLabel(alertItem);
    const currentAlertData = currentData?.[alertKey] || [];

    if (currentData && Number.isFinite(currentData[trendingLineKey])) {
      mergedDataByTime[formattedStartTime] = {
        ...currentData,
        [alertKey]: [...currentAlertData, alertItem],
      };
    }
  }

  for (const bedOccupancyItem of bedOccupancyDataPointsCounts) {
    const formattedTime = displayInTimezone(
      bedOccupancyItem.time,
      timezone,
      POINTS_DATE_FORMAT,
    );

    const currentData = mergedDataByTime[formattedTime];
    const patientStatus = getPatientStatus(
      currentData as TrendingOverviewItemData,
      bedOccupancyItem,
    );
    // TODO: For patient out of bed, all the values should be N/A
    if (patientStatus === PATIENT_STATUS.OUT_OF_BED) {
      mergedDataByTime[formattedTime] = {
        anchorLine: 0,
        patientStatus,
        time: formattedTime,
      };
    } else {
      mergedDataByTime[formattedTime] = {
        ...currentData,
        patientStatus,
        time: formattedTime,
      };
    }
  }

  return mergedDataByTime;
};

const mergeNewMedicalReportDataByTime = ({
  timezone,
  hrData,
  rrData,
  ieData,
  raData,
  motionData = [],
  alertLogsData = [],
  bedOccupancyDataPointsCounts = [],
}: {
  timezone: string;
  hrData: ContinuousAggregatedMeasurement[];
  rrData: ContinuousAggregatedMeasurement[];
  ieData: ContinuousAggregatedMeasurement[];
  raData: ContinuousAggregatedMeasurement[];
  motionData?: ContinousMotionAggregation[];
  alertLogsData?: AlertLog[];
  bedOccupancyDataPointsCounts?: BedOccupancyDataPointsCount[];
}) => {
  const mergedDataByTime: Record<string, TrendingOverviewItemData> = {};
  const aggregatedMotionData = motionData;

  for (const hrItem of hrData) {
    const formattedTime = displayInTimezone(
      hrItem.time,
      timezone,
      POINTS_DATE_FORMAT,
    );
    const currentData = mergedDataByTime[formattedTime];
    if (checkMeasurementIsValid(hrItem.average, MeasurementType.HR)) {
      mergedDataByTime[formattedTime] = {
        ...currentData,
        anchorLine: 0,
        time: formattedTime,
        hr: Math.round(hrItem.average),
      };
    }
  }

  for (const rrItem of rrData) {
    const formattedTime = displayInTimezone(
      rrItem.time,
      timezone,
      POINTS_DATE_FORMAT,
    );
    const currentData = mergedDataByTime[formattedTime];
    if (checkMeasurementIsValid(rrItem.average, MeasurementType.RR)) {
      mergedDataByTime[formattedTime] = {
        ...currentData,
        time: formattedTime,
        anchorLine: 0,
        rr: Math.round(rrItem.average),
      };
    }
  }

  for (const ieItem of ieData) {
    const formattedTime = displayInTimezone(
      ieItem.time,
      timezone,
      POINTS_DATE_FORMAT,
    );
    const currentData = mergedDataByTime[formattedTime];
    if (checkMeasurementIsValid(ieItem.average, MeasurementType.IE)) {
      mergedDataByTime[formattedTime] = {
        ...currentData,
        time: formattedTime,
        anchorLine: 0,
        ie: Math.round(ieItem.average),
      };
    }
  }

  for (const raItem of raData) {
    const formattedTime = displayInTimezone(
      raItem.time,
      timezone,
      POINTS_DATE_FORMAT,
    );
    const currentData = mergedDataByTime[formattedTime];
    mergedDataByTime[formattedTime] = {
      ...currentData,
      time: formattedTime,
      anchorLine: 0,
      ra: Math.round(raItem.average),
    };
  }

  for (const motionItem of aggregatedMotionData) {
    const formattedTime = displayInTimezone(
      motionItem.time,
      timezone,
      POINTS_DATE_FORMAT,
    );
    const currentData = mergedDataByTime[formattedTime];
    if (motionItem) {
      mergedDataByTime[formattedTime] = {
        ...currentData,
        time: motionItem.time,
        anchorLine: 0,
        motion: motionItem.aggregatedValue,
      };
    }
  }

  for (const alertItem of alertLogsData) {
    const startTime = roundToNearest5Minutes(alertItem.startTime).format(
      POINTS_DATE_FORMAT,
    );
    const currentData =
      mergedDataByTime[
        displayInTimezone(startTime, timezone, POINTS_DATE_FORMAT)
      ];
    const alertKey = mapAlertToChartMarkLabel(alertItem);
    const trendingLineKey = mapAlertToTrendLineLabel(alertItem);
    const currentAlertData = currentData?.[alertKey] || [];

    if (currentData && Number.isFinite(currentData[trendingLineKey])) {
      mergedDataByTime[
        displayInTimezone(startTime, timezone, POINTS_DATE_FORMAT)
      ] = {
        ...currentData,
        anchorLine: 0,
        [alertKey]: [...currentAlertData, alertItem],
      };
    }
  }

  for (const bedOccupancyItem of bedOccupancyDataPointsCounts) {
    const formattedTime = displayInTimezone(
      bedOccupancyItem.time,
      timezone,
      POINTS_DATE_FORMAT,
    );
    const currentData = mergedDataByTime[formattedTime];
    const patientStatus = getPatientStatus(
      currentData as TrendingOverviewItemData,
      bedOccupancyItem,
    );
    // TODO: For patient out of bed, all the values should be N/A
    if (patientStatus === PATIENT_STATUS.OUT_OF_BED) {
      mergedDataByTime[formattedTime] = {
        anchorLine: 0,
        patientStatus,
        time: formattedTime,
      };
    } else {
      mergedDataByTime[formattedTime] = {
        ...currentData,
        patientStatus,
        time: formattedTime,
      };
    }
  }

  return mergedDataByTime;
};

const mergeOldMedicalReportDataByTime = ({
  timeValues,
  timezone,
  hrData,
  rrData,
  ieData,
  raData,
  motionData = [],
  alertLogsData = [],
  bedOccupancyDataPointsCounts = [],
  stepSize,
  unit = 'hour',
}: {
  timeValues: string[];
  timezone: string;
  hrData: ContinuousAggregatedMeasurement[];
  rrData: ContinuousAggregatedMeasurement[];
  ieData: ContinuousAggregatedMeasurement[];
  raData: ContinuousAggregatedMeasurement[];
  motionData?: ContinousMotionAggregation[];
  alertLogsData?: AlertLog[];
  bedOccupancyDataPointsCounts?: BedOccupancyDataPointsCount[];
  stepSize: number;
  unit?: ManipulateType;
}) => {
  const mergedDataByTime: Record<
    string,
    TrendingOverviewItemData & {
      order: number;
    }
  > = initializeMergedData(timeValues, timezone);
  const stepSizeInHours = convertToHourResolutionStepSize(stepSize, unit);
  const aggregatedMotionData = aggregateMotionData(motionData, stepSizeInHours);
  for (const hrItem of hrData) {
    const currentData = mergedDataByTime[hrItem.time];
    if (
      currentData &&
      checkMeasurementIsValid(hrItem.average, MeasurementType.HR)
    ) {
      mergedDataByTime[hrItem.time] = {
        ...currentData,
        hr: Math.round(hrItem.average),
      };
    }
  }

  for (const rrItem of rrData) {
    const currentData = mergedDataByTime[rrItem.time];
    if (
      currentData &&
      checkMeasurementIsValid(rrItem.average, MeasurementType.RR)
    ) {
      mergedDataByTime[rrItem.time] = {
        ...currentData,
        rr: Math.round(rrItem.average),
      };
    }
  }

  for (const ieItem of ieData) {
    const currentData = mergedDataByTime[ieItem.time];
    if (
      currentData &&
      checkMeasurementIsValid(ieItem.average, MeasurementType.IE)
    ) {
      mergedDataByTime[ieItem.time] = {
        ...currentData,
        ie: Math.round(ieItem.average),
      };
    }
  }

  for (const raItem of raData) {
    const currentData = mergedDataByTime[raItem.time];
    if (currentData) {
      mergedDataByTime[raItem.time] = {
        ...currentData,
        ra: Math.round(raItem.average),
      };
    }
  }

  for (const motionAggregatedTime of Object.keys(aggregatedMotionData)) {
    const currentData = mergedDataByTime[motionAggregatedTime];

    if (currentData && Number.isInteger(currentData.rr)) {
      mergedDataByTime[motionAggregatedTime] = {
        ...currentData,
        motion: aggregatedMotionData[motionAggregatedTime],
      };
    }
  }

  for (const alertItem of alertLogsData) {
    const startTime = mapUtcStringTimeToHourResolution(
      alertItem.startTime,
      stepSizeInHours,
      'start',
    );
    const currentData = mergedDataByTime[startTime];
    const alertKey = mapAlertToChartMarkLabel(alertItem);
    const trendingLineKey = mapAlertToTrendLineLabel(alertItem);
    const currentAlertData = currentData?.[alertKey] || [];

    if (currentData && Number.isFinite(currentData[trendingLineKey])) {
      mergedDataByTime[startTime] = {
        ...currentData,
        [alertKey]: [...currentAlertData, alertItem],
      };
    }
  }

  for (const bedOccupancyItem of bedOccupancyDataPointsCounts) {
    const currentData = mergedDataByTime[bedOccupancyItem.time];
    if (currentData) {
      mergedDataByTime[bedOccupancyItem.time] = {
        ...currentData,
        patientStatus: getPatientStatus(currentData, bedOccupancyItem),
      };
    }
  }

  return mergedDataByTime;
};

export const mergeMedicalReportsChartData = ({
  hrData,
  rrData,
  ieData,
  raData,
  motionData = [],
  alertLogsData = [],
  bedOccupancyDataPointsCounts = [],
  timezone,
  startTime,
  endTime,
  stepSize,
  unit,
  version,
}: {
  hrData: ContinuousAggregatedMeasurement[];
  rrData: ContinuousAggregatedMeasurement[];
  ieData: ContinuousAggregatedMeasurement[];
  raData: ContinuousAggregatedMeasurement[];
  motionData?: ContinousMotionAggregation[];
  alertLogsData?: AlertLog[];
  bedOccupancyDataPointsCounts?: BedOccupancyDataPointsCount[];
  timezone: string;
  startTime: Dayjs;
  endTime: Dayjs;
  stepSize: number;
  version: number | null;
  unit?: ManipulateType;
}): TrendingOverviewItemData[] => {
  const timeValues: string[] = computeTimeValues(
    startTime,
    endTime,
    stepSize,
    unit,
  );
  const mergedDataByTime =
    version === MEDICAL_REPORT_API_VERSION.OLD
      ? mergeOldMedicalReportDataByTime({
          timeValues,
          timezone,
          hrData,
          rrData,
          ieData,
          raData,
          motionData,
          alertLogsData,
          bedOccupancyDataPointsCounts,
          stepSize,
          unit,
        })
      : mergeNewMedicalReportDataByTime({
          timezone,
          hrData,
          rrData,
          ieData,
          raData,
          motionData,
          alertLogsData,
          bedOccupancyDataPointsCounts,
        });
  const mergedDataValues = Object.values(mergedDataByTime);
  const res = mergedDataValues
    .sort((item1: TrendingOverviewItemData, item2: TrendingOverviewItemData) =>
      dayjs(item1.time).diff(item2.time, 'minute') > 0 ? 1 : -1,
    )
    .map(item => omit(item, 'order'));
  return res;
};

export const mergeChartData = ({
  measurementsData,
  motionData = [],
  alertLogsData = [],
  bedOccupancyDataPointsCounts = [],
  timezone,
}: {
  measurementsData?: FetchVitalSignsMeasurementsResponse;
  motionData?: ContinousMotionAggregation[];
  alertLogsData?: AlertLog[];
  bedOccupancyDataPointsCounts?: BedOccupancyDataPointsCount[];
  timezone: string;
}): TrendingOverviewItemData[] => {
  const mergedDataByTime = mergeDataByTime({
    timezone,
    measurementsData,
    motionData,
    alertLogsData,
    bedOccupancyDataPointsCounts,
  });
  const mergedDataValues = Object.values(mergedDataByTime);

  const res = mergedDataValues
    .sort((item1: TrendingOverviewItemData, item2: TrendingOverviewItemData) =>
      dayjs(item1.time).diff(item2.time, 'minute'),
    )
    .map(item => omit(item, 'order'));

  return res;
};

export const computePositionChanges = (
  positionData: ContinousPositionCount[] = [],
  timezone: string,
  chartData: TrendingOverviewItemData[],
): string[] => {
  const visiblePositionChanges: string[] = [];

  for (const item of positionData) {
    const correctedTime = dayjs
      .utc(item.time)
      .startOf('hour')
      .tz(timezone)
      .format(POINTS_DATE_FORMAT);
    const currentChartData = chartData.find(
      chartItem => chartItem.time === correctedTime,
    );

    if (
      item.count &&
      !visiblePositionChanges.includes(correctedTime) &&
      currentChartData?.motion
    ) {
      visiblePositionChanges.push(correctedTime);
    }
  }

  return visiblePositionChanges;
};

export const countDataPoints = (
  hrRawContinuousData: ContinuousAggregatedMeasurement[],
  rrRawContinuousData: ContinuousAggregatedMeasurement[],
): number =>
  [...hrRawContinuousData, ...rrRawContinuousData].reduce(
    (acc, entry) => acc + entry.dataPointsCount,
    0,
  );

export const computeIntervalAverage = (
  rawContinuousData: ContinuousAggregatedMeasurement[],
): number => {
  const sum = rawContinuousData.reduce((acc, item) => acc + item.average, 0);

  return Math.round(sum / rawContinuousData.length);
};
export const computeIntervalAverageForPatientChart = (
  measurements?: FetchVitalSignsMeasurementsResponse,
) => {
  let hrSum = null,
    rrSum = null,
    hrCount = null,
    rrCount = null;
  if (measurements?.measurements) {
    for (const measure of measurements.measurements) {
      const { HR, RR } = measure.measurements;
      if (!isNullOrUndefined<number>(HR?.average)) {
        if (hrSum === null) hrSum = 0;
        if (hrCount === null) hrCount = 0;
        hrSum += HR?.average ?? 0;
        hrCount++;
      }
      if (!isNullOrUndefined<number>(RR?.average)) {
        if (rrSum === null) rrSum = 0;
        if (rrCount === null) rrCount = 0;
        rrSum += RR?.average ?? 0;
        rrCount++;
      }
    }
  }
  return {
    hr: hrCount && hrSum ? Math.round(hrSum / hrCount) : hrCount,
    rr: rrCount && rrSum ? Math.round(rrSum / rrCount) : rrCount,
  };
};
export const countDataPointsForPatientChart = (
  measurements?: FetchVitalSignsMeasurementsResponse,
): number => {
  let count = 0;
  if (measurements?.measurements) {
    for (const measure of measurements.measurements) {
      const { HR, RR } = measure.measurements;
      count += (HR?.dataPointsCount ?? 0) + (RR?.dataPointsCount ?? 0);
    }
  }
  return count;
};

export const getTimeFrameByRange = (range: TIMEFRAME_RANGE) => ({
  range,
  startTime: getStartTimeOfRange(range),
  endTime: new Date().toISOString(),
});

export const getTimeFrameByInterval = (
  startTime: DateUTC,
  endTime: DateUTC,
) => ({
  range: TIMEFRAME_RANGE.CUSTOM,
  startTime,
  endTime,
});
